import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Download } from "lucide-react";

export default function AudioPlayer({ audioUrl, onDownload }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    setPlaying(false);
    setProgress(0);
  }, [audioUrl]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play();
      setPlaying(true);
    }
  };

  const onTime = () => {
    const a = audioRef.current;
    if (!a) return;
    setProgress((a.currentTime / (a.duration || 1)) * 100);
  };

  const onLoaded = () => {
    const a = audioRef.current;
    if (!a) return;
    setDuration(a.duration || 0);
  };

  const fmt = (s) => {
    if (!isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = pct * a.duration;
  };

  const bars = 40;
  const currentBar = Math.floor((progress / 100) * bars);

  return (
    <div
      className="bg-black text-white p-5 border border-black"
      data-testid="audio-player"
    >
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={onTime}
        onLoadedMetadata={onLoaded}
        onEnded={() => setPlaying(false)}
      />
      <div className="flex items-center gap-4">
        <button
          onClick={toggle}
          className="w-12 h-12 bg-[#FF4500] hover:bg-[#E03C00] text-white flex items-center justify-center transition-colors shrink-0"
          data-testid="audio-play-btn"
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause size={20} fill="white" /> : <Play size={20} fill="white" />}
        </button>

        <div
          className="flex-1 h-12 flex items-center gap-[2px] cursor-pointer select-none"
          onClick={seek}
          data-testid="audio-waveform"
        >
          {Array.from({ length: bars }).map((_, i) => {
            const h = 20 + ((i * 37) % 80);
            const active = i <= currentBar;
            return (
              <div
                key={i}
                className={`flex-1 ${active ? "bg-[#FF4500]" : "bg-white/30"}`}
                style={{ height: `${h}%` }}
              />
            );
          })}
        </div>

        <div
          className="font-mono text-xs tabular-nums text-white/70 shrink-0 w-20 text-right"
          data-testid="audio-time"
        >
          {fmt((audioRef.current?.currentTime || 0))} / {fmt(duration)}
        </div>

        {onDownload && (
          <button
            onClick={onDownload}
            className="w-10 h-10 border border-white/20 hover:bg-white hover:text-black flex items-center justify-center transition-colors shrink-0"
            title="Download"
            data-testid="audio-download-btn"
          >
            <Download size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
